import { AppDispatch } from 'store';
import { messageApi } from './messageApi';
import { tradeApi } from '../trade/tradeApi';
import type {
  NewMessagePayload,
  Message,
  Conversation,
} from 'types/interfaces/message';

/**
 * Update inbox cache when a new message is received via WebSocket.
 * Either updates an existing conversation or prepends a new one.
 */
export const updateInboxWithNewMessage = (
  dispatch: AppDispatch,
  payload: NewMessagePayload
) => {
  // Update the inbox query cache for page 1 (most common case)
  dispatch(
    messageApi.util.updateQueryData('getInbox', { page: 1, pageSize: 25 }, (draft) => {
      const existingIndex = draft.conversations.findIndex(
        (c) => c.conversationId === payload.conversationId
      );

      if (existingIndex >= 0) {
        // Update existing conversation
        const conv = draft.conversations[existingIndex];
        conv.lastMessage = payload.content;
        conv.lastMessageAt = payload.createdAt;
        conv.unreadCount += 1;
        conv.isRead = false;
        // Move to top
        draft.conversations.splice(existingIndex, 1);
        draft.conversations.unshift(conv);
      } else {
        // Prepend new conversation
        const newConversation: Conversation = {
          conversationId: payload.conversationId,
          otherUser: {
            id: payload.sender.id,
            username: payload.sender.username,
            avatarUrl: payload.sender.avatarUrl,
            country: payload.sender.country,
          },
          lastMessage: payload.content,
          lastMessageAt: payload.createdAt,
          unreadCount: 1,
          isRead: false,
        };
        draft.conversations.unshift(newConversation);
      }
    })
  );
};

/**
 * Update a specific conversation thread with a new message.
 * Used when viewing a conversation and receiving a new message.
 */
export const updateThreadWithNewMessage = (
  dispatch: AppDispatch,
  conversationId: string,
  payload: NewMessagePayload
) => {
  dispatch(
    messageApi.util.updateQueryData('getConversationThread', conversationId, (draft) => {
      // Convert payload to Message format
      const newMessage: Message = {
        id: payload.messageId,
        sender: {
          id: payload.sender.id,
          username: payload.sender.username,
          avatarUrl: payload.sender.avatarUrl,
          country: payload.sender.country,
        },
        content: payload.content,
        createdAt: payload.createdAt,
        isRead: false,
        isSystemMessage: payload.type === 'SYSTEM',
      };
      draft.messages.push(newMessage);
    })
  );
};

/**
 * Update the unread count badge.
 */
export const updateUnreadCount = (dispatch: AppDispatch, count: number) => {
  dispatch(
    messageApi.util.updateQueryData('getUnreadCount', undefined, (draft) => {
      draft.count = count;
    })
  );
};

/**
 * Update the inbound trade count badge.
 */
export const updateInboundTradeCount = (
  dispatch: AppDispatch,
  count: number,
  hasOverdue: boolean
) => {
  dispatch(
    tradeApi.util.updateQueryData('getInboundTradeCount', undefined, (draft) => {
      draft.count = count;
      draft.hasOverdueTrades = hasOverdue;
    })
  );
};

/**
 * Update message read status in a conversation thread.
 * Called when sender receives read receipt from recipient.
 */
export const updateMessageReadStatus = (
  dispatch: AppDispatch,
  conversationId: string,
  messageIds: string[],
  readAt: string
) => {
  dispatch(
    messageApi.util.updateQueryData('getConversationThread', conversationId, (draft) => {
      draft.messages.forEach((msg) => {
        if (messageIds.includes(msg.id)) {
          msg.isRead = true;
          // Add readAt timestamp for read receipt display
          (msg as Message & { readAt?: string }).readAt = readAt;
        }
      });
    })
  );
};

/**
 * Mark a conversation as read in the inbox list.
 * Called when viewing a conversation.
 */
export const markConversationAsReadInCache = (
  dispatch: AppDispatch,
  conversationId: string
) => {
  dispatch(
    messageApi.util.updateQueryData('getInbox', { page: 1, pageSize: 25 }, (draft) => {
      const conv = draft.conversations.find((c) => c.conversationId === conversationId);
      if (conv) {
        conv.unreadCount = 0;
        conv.isRead = true;
      }
    })
  );
};
